<template>
  <div>
    <div class="traffic-light-line">

      <div class="labeled-line">
        <img v-if="canMoveToPastCashflow" class="right-triangle" v-on:click="moveToNextBudgetMonth()"
             :src="require('@/assets/icons/right-triangle.svg')">
        <div class="label out-of-date">
          {{ fullMonthName }}
        </div>
        <div class="line out-of-date"></div>
        <div v-if="isPresentingLatestMonth" class="circle out-of-date"></div>
        <img v-if="!isPresentingLatestMonth" class="left-triangle" v-on:click="moveToPrevBudgetMonth()"
             :src="require('@/assets/icons/left-triangle.svg')">
      </div>
    </div>
    <div class="out-of-date-cashflow-view-container">
      <template v-if="isCurrentMonth">
        <weekly-variables-summary v-if="isPredictedCashflowPositive"/>
        <expected-end-of-month v-else/>
        <riseup-goal/>
      </template>
      <cashflow-type-weekly-variables/>
      <cashflow-type-economy/>
      <div v-for="expenses in recurringExpenses" v-bind:key="expenses.expenseCategory">
        <cashflow-type-recurring :expenses=expenses :expenseCategory="expenses.expenseCategory"/>
      </div>
      <div v-for="subtree in trackingCategories" v-bind:key="subtree.categoryId">
        <cashflow-type-tracking-category :subtree="subtree" />
      </div>
      <cashflow-type-fixed-expense/>
      <cashflow-type-income/>
    </div>
    <div class="out-of-date-cashflow-view-container">
      <hr class="divider" v-if="this.hasExcluded" />
      <cashflow-type-excluded v-if="this.hasExcluded"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Segment from '../../../../../../../Segment';
import ExpectedEndOfMonth from './components/ExpectedEndOfMonth.vue';
import RiseupGoal from './components/RiseupGoal.vue';
import CashflowTypeIncome from './components/types/CashflowTypeIncome.vue';
import CashflowTypeExcluded from './components/types/CashflowTypeExcluded.vue';
import CashflowTypeEconomy from './components/types/CashflowTypeEconomy.vue';
import CashflowTypeRecurring from './components/types/CashflowTypeRecurring.vue';
import CashflowTypeTrackingCategory from './components/types/CashflowTypeTrackingCategory.vue';
import CashflowTypeFixedExpense from './components/types/CashflowTypeFixedExpense.vue';
import CashflowTypeWeeklyVariables from './components/types/CashflowTypeWeeklyVariables.vue';
import WeeklyVariablesSummary from './components/WeeklyVariablesSummary.vue';
import cashflowViewConsts from '../../../../../../../constants/cashflow-view';

export default {
  name: 'OutOfDateCashflowView',
  components: {
    WeeklyVariablesSummary,
    CashflowTypeWeeklyVariables,
    RiseupGoal,
    ExpectedEndOfMonth,
    CashflowTypeIncome,
    CashflowTypeExcluded,
    CashflowTypeEconomy,
    CashflowTypeFixedExpense,
    CashflowTypeRecurring,
    CashflowTypeTrackingCategory,
  },
  async created() {
    Segment.trackUserGot('CurrentCashflowViewEntered');
  },
  computed: {
    ...mapState('cashflowView', ['cashflowUITree', 'presentedBudgetDate', 'currentBudgetDate', 'presentedBudgets']),
    ...mapGetters('cashflowView', ['fullMonthName', 'predictedBalanceAtEndOfMonth', 'remainingVariables', 'hasExcluded', 'isPresentingLatestMonth']),
    isPredictedCashflowPositive() {
      return this.predictedBalanceAtEndOfMonth > 0;
    },
    hasRemainingVariables() {
      return this.remainingVariables > 0;
    },
    canMoveToPastCashflow() {
      const lastBudget = _.last(this.presentedBudgets);
      return lastBudget && this.presentedBudgetDate !== lastBudget.budgetDate;
    },
    isCurrentMonth() {
      return this.presentedBudgetDate === this.currentBudgetDate;
    },
    recurringExpenses() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.recurring];
    },
    trackingCategories() {
      return this.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory];
    },
  },
  methods: {
    ...mapActions('cashflowView', ['moveToNextBudgetMonth', 'moveToPrevBudgetMonth']),
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../scss/mixins';

  @keyframes shake {
    0% { transform: translate(2px, 0); }
    50% { transform: translate(-2px, 0); }
    100% { transform: translate(2px, 0); }
  }

  .traffic-light-line {
    @include responsive-padding;
    width: 100%;
    text-align: center;
    margin-bottom: 11px;

    .labeled-line {
      width: 100%;
      padding-top: 12px;
      position: relative;
      display: inline-block;

      .out-of-date {
        background-color: $riseup_gray_2;
      }

      .label {
        height: 18px;
        line-height: 15px;
        font-size: 14px;
        padding: 2px 4px 2px 4px;
        display: inline-block;
        color: $riseup_white;
        font-weight: bold;
        position: absolute;
        right: 0;
        top: -3px;
      }

      .line {
        height: 3px;
      }

      .circle {
        position: absolute;
        left: 0;
        top: 5px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        display: inline-block;
        background-color: $riseup_gray_2;
      }

      .right-triangle {
        position: absolute;
        right: -20px;
        top: -4px;
      }

      .left-triangle {
        position: absolute;
        left: -20px;
        top: -4px;
      }
    }
  }

  .divider {
    color: $riseup_gray_2;
    opacity: 0.5;
    margin: 25px 0;

    &.tight {
      margin-bottom: 0;
    }
  }

  .out-of-date-cashflow-view-container {
    @include responsive-padding;
    font-family: SimplerPro;
    text-align: right;
    width: 100%;
    margin: 0 auto;
  }
</style>
